import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useLatest } from 'react-use'
import { AxiosError } from 'axios'
import ContentCutIcon from '@mui/icons-material/ContentCut'
import { useStartConfiguredWallAlignment } from '@api/wall-alignment/wall-alignment'
import { Form2 } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { CollapsibleBoxInternalState } from '@ui/structure'
import {
  useControlStore,
  useControlStoreApi,
  useElementSelectionStore,
  useModelStore,
} from '@editorStores'
import { useModelClickListeners, useResultsInvalidation, useSelectionMode } from '@editorHooks'
import { buildErrorMessage } from 'src/constants/errors'
import { retryUntilSuccessOrFailure } from 'src/state/utils'
import { useModelInvalidation } from '../../../../hooks'
import ConfiguredWallCutFields, {
  ConfiguredWallCutFormValues,
} from './components/ConfiguredWallCutFields'
import { defaultLowerStorey, defaultUpperStorey, NO_UPPER_STOREY } from './utils'

const CONFIGURED_WALL_CUT_SELECTION_KEY = 'configured-wall-cut-slabs'

interface Props {
  wallGuid: string
  wallStorey: string
}

const ConfiguredWallAlignmentForm = ({ wallGuid, wallStorey }: Props): ReactElement | null => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const storeyBoundaries = useModelStore(state => state.model.storey_boundaries)
  const roofSlabs = useModelStore(state => state.model.roof_slabs)
  const invalidateModel = useModelInvalidation()
  const invalidateResults = useResultsInvalidation()

  const { setSelectionMode, selectionModeKey } = useSelectionMode()
  const controlStoreApi = useControlStoreApi()
  const setHighlightedIds = useElementSelectionStore(state => state.setHighlightedIds)
  const resetHighlightedIds = useElementSelectionStore(state => state.resetHighlightedIds)
  const problemViewActiveErrors = useControlStore(state => state.problemViewActiveErrors)
  const problemViewActiveWarnings = useControlStore(state => state.problemViewActiveWarnings)
  const setProblemViewActiveErrors = useControlStore(state => state.setProblemViewActiveErrors)
  const setProblemViewActiveWarnings = useControlStore(state => state.setProblemViewActiveWarnings)

  const [roofSlabGuids, setRoofSlabGuids] = useState<Set<string>>(() => new Set())
  const roofSlabGuidsRef = useLatest(roofSlabGuids)
  const isPickingRoofSlabs = selectionModeKey === CONFIGURED_WALL_CUT_SELECTION_KEY

  const replaceRoofSlabs = useCallback(
    (next: Set<string>) => {
      setRoofSlabGuids(next)
      if (controlStoreApi.getState().selectionModeKey === CONFIGURED_WALL_CUT_SELECTION_KEY) {
        setHighlightedIds(Array.from(next))
      }
    },
    [controlStoreApi, setHighlightedIds],
  )

  const toggleRoofSlab = useCallback(
    (event: { object: { name: string; userData?: { elementType?: string } } }) => {
      const guid = event.object.name
      if (event.object.userData?.elementType !== 'roof_slabs') return
      const next = new Set(roofSlabGuidsRef.current)
      if (next.has(guid)) next.delete(guid)
      else next.add(guid)
      replaceRoofSlabs(next)
    },
    [replaceRoofSlabs, roofSlabGuidsRef],
  )

  useModelClickListeners(toggleRoofSlab, [toggleRoofSlab], isPickingRoofSlabs)

  useEffect(() => {
    return () => {
      resetHighlightedIds()
      const state = controlStoreApi.getState()
      if (state.selectionModeKey === CONFIGURED_WALL_CUT_SELECTION_KEY) {
        state.toggleSelectionMode()
        state.selectionAbortCallback?.()
      }
    }
  }, [controlStoreApi, resetHighlightedIds])

  const exitSelectionMode = useCallback(() => {
    const state = controlStoreApi.getState()
    if (state.selectionModeKey !== CONFIGURED_WALL_CUT_SELECTION_KEY) return
    state.toggleSelectionMode()
    state.selectionAbortCallback?.()
  }, [controlStoreApi])

  const enterSelectionMode = useCallback(
    (highlightIds?: string[]) => {
      const ids = highlightIds ?? Array.from(roofSlabGuidsRef.current)
      if (controlStoreApi.getState().selectionModeKey === CONFIGURED_WALL_CUT_SELECTION_KEY) {
        setHighlightedIds(ids)
        return
      }
      const previous = {
        errors: problemViewActiveErrors,
        warnings: problemViewActiveWarnings,
      }
      setProblemViewActiveErrors(false)
      setProblemViewActiveWarnings(false)
      setSelectionMode({
        key: CONFIGURED_WALL_CUT_SELECTION_KEY,
        message:
          'Klicken Sie im Modell auf Dachflächen, an denen die Wand zugeschnitten werden soll. Schließen Sie diese Meldung zum Beenden.',
        onAbort: () => {
          setProblemViewActiveErrors(previous.errors)
          setProblemViewActiveWarnings(previous.warnings)
          resetHighlightedIds()
        },
      })
      setHighlightedIds(ids)
    },
    [
      controlStoreApi,
      problemViewActiveErrors,
      problemViewActiveWarnings,
      resetHighlightedIds,
      roofSlabGuidsRef,
      setHighlightedIds,
      setProblemViewActiveErrors,
      setProblemViewActiveWarnings,
      setSelectionMode,
    ],
  )

  const onRemoveRoofSlab = useCallback(
    (guid: string) => {
      const next = new Set(roofSlabGuidsRef.current)
      next.delete(guid)
      replaceRoofSlabs(next)
    },
    [replaceRoofSlabs, roofSlabGuidsRef],
  )

  const onSelectAllRoofSlabs = useCallback(() => {
    const next = roofSlabs.map(slab => slab.guid)
    setRoofSlabGuids(new Set(next))
    enterSelectionMode(next)
  }, [enterSelectionMode, roofSlabs])

  const onDeselectAllRoofSlabs = useCallback(() => {
    setRoofSlabGuids(new Set())
    exitSelectionMode()
    resetHighlightedIds()
  }, [exitSelectionMode, resetHighlightedIds])

  const selectAllDisabled =
    roofSlabs.length === 0 ||
    (isPickingRoofSlabs && roofSlabs.every(slab => roofSlabGuids.has(slab.guid)))

  const { mutateAsync, isLoading } = useStartConfiguredWallAlignment<AxiosError>({
    mutation: {
      meta: {
        title: 'Wand zuschneiden',
        description: 'Wand an Dachflächen und Geschossgrenzen zuschneiden',
      },
    },
  })

  const onSubmit = useCallback(
    async (values: ConfiguredWallCutFormValues) => {
      exitSelectionMode()
      try {
        const response = await mutateAsync({
          projectGuid: projectId as string,
          wallGuid,
          data: {
            lowerStorey: Number(values.lowerStorey),
            upperStorey: values.upperStorey === NO_UPPER_STOREY ? null : Number(values.upperStorey),
            roofSlabGuids: Array.from(roofSlabGuidsRef.current),
          },
        })
        await retryUntilSuccessOrFailure({
          url: `/task-status/${response.task_id}`,
        })
        await invalidateModel(projectId as string)
        invalidateResults(projectId as string)
        enqueueSnackbar('Wand erfolgreich zugeschnitten', { variant: 'success' })
      } catch (error) {
        invalidateModel(projectId as string)
        const resolved = error instanceof Error ? error : new Error('Unbekannter Fehler')
        enqueueSnackbar(buildErrorMessage(resolved, 'Fehler beim Zuschneiden der Wand'), {
          variant: 'error',
        })
      }
    },
    [
      enqueueSnackbar,
      invalidateModel,
      invalidateResults,
      mutateAsync,
      exitSelectionMode,
      projectId,
      roofSlabGuidsRef,
      wallGuid,
    ],
  )

  const defaultValues = useMemo<ConfiguredWallCutFormValues>(
    () => ({
      lowerStorey: defaultLowerStorey(storeyBoundaries, wallStorey),
      upperStorey: defaultUpperStorey(storeyBoundaries, wallStorey),
    }),
    [storeyBoundaries, wallStorey],
  )

  if (Object.keys(storeyBoundaries).length === 0) return null

  return (
    <CollapsibleBoxInternalState
      variant="expander"
      label="Zuschneiden"
      defaultExpanded={false}
      startIcon={<ContentCutIcon sx={{ color: 'grey.600', padding: '2px' }} />}
      dataCy="accordion-configured-wall-cut"
    >
      <Form2<ConfiguredWallCutFormValues>
        id={`configured-wall-cut-${wallGuid}`}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        data-cy="form-configured-wall-cut"
      >
        <ConfiguredWallCutFields
          roofSlabGuids={Array.from(roofSlabGuids)}
          onRemoveRoofSlab={onRemoveRoofSlab}
          onStartSelecting={() => enterSelectionMode()}
          onSelectAllRoofSlabs={onSelectAllRoofSlabs}
          onDeselectAllRoofSlabs={onDeselectAllRoofSlabs}
          selectAllDisabled={selectAllDisabled}
          isSelectingRoofSlabs={isPickingRoofSlabs}
          onSubmit={onSubmit}
          isLoading={isLoading}
          storeyBoundaries={storeyBoundaries}
        />
      </Form2>
    </CollapsibleBoxInternalState>
  )
}

export default ConfiguredWallAlignmentForm
